/**
 * The landing scene's LAYERS: what the garden is made of, and how one layer is
 * drawn. Split from LandingScreen.js when the foreground plane took it past
 * the 350-line contract — the screen composes a page, this file describes a
 * scene, and they change for different reasons.
 *
 * The CSS counterpart of this split is landingScene.css (the stage and the
 * reference plane) and landingPlanting.css (what stands on it); a layer's
 * `key` here is what both address it by.
 */

/**
 * The landing screen (FR-02): the proposition, one primary action, and the
 * botanical hero behind both — recomposed by SPEC-021 as a garden doorway: a
 * paved path entering at the bottom of frame, beds flanking it, wildlife
 * moving through passages of sun and shade.
 *
 * The scene is built from INDEPENDENT layers rather than one flattened image
 * (NFR-03), so each can move at its own depth. Every layer is decorative: the
 * headline carries the meaning, so the whole scene is `aria-hidden` and the
 * images take an empty alt. Describing a mood in alt text would make a screen
 * reader slower without making it better informed.
 *
 * Depth runs 0 (far) → beyond 1 (nearer than the reference plane). The value
 * feeds the parallax translation, so a layer's distance is declared once, and
 * the array is ordered BY that distance — paint order is the depth sort.
 * Every layer is cut from the one `vector-*` kit (SPEC-021 D-1); the insects'
 * keyframes depend on the facing recorded in scripts/build-landing-assets.js.
 */

export const SCENE_LAYERS = [
    {
        key: 'garden',
        depth: 0.15,
        widths: [512, 768, 1024],
        group: 'backgrounds',
        name: 'hero-garden',
        sizes: 'min(112vw, 717px)',
    },
    {
        key: 'lupine',
        depth: 0.5,
        widths: [150, 300],
        group: 'layers',
        name: 'lupine',
        sizes: '(max-width: 640px) 19vw, 118px',
    },
    {
        key: 'butterfly',
        depth: 0.55,
        widths: [75, 150],
        group: 'layers',
        name: 'butterfly',
        sizes: '(max-width: 640px) 10vw, 50px',
    },
    {
        key: 'coneflower',
        depth: 0.75,
        widths: [200, 400],
        group: 'layers',
        name: 'coneflower',
        sizes: '(max-width: 640px) 26vw, 155px',
    },
    {
        key: 'lavender',
        depth: 0.8,
        widths: [230, 460],
        group: 'layers',
        name: 'lavender',
        sizes: '(max-width: 640px) 30vw, 180px',
    },
    {
        // Behind the near grasses on purpose: a dragonfly half-veiled by the
        // plumes it patrols is what makes the front plane read as IN FRONT.
        key: 'dragonfly',
        depth: 0.9,
        widths: [70, 140],
        group: 'layers',
        name: 'dragonfly',
        sizes: '(max-width: 640px) 9vw, 46px',
    },
    {
        key: 'daisy',
        depth: 1,
        widths: [170, 340],
        group: 'layers',
        name: 'daisy',
        sizes: '(max-width: 640px) 22vw, 130px',
    },
    {
        key: 'bee',
        depth: 1.05,
        widths: [55, 110],
        group: 'layers',
        name: 'bee',
        sizes: '(max-width: 640px) 7vw, 36px',
    },
];

/**
 * The nearest plane, split out of SCENE_LAYERS so it can be rendered AFTER the
 * hero and paint IN FRONT of the call to action.
 *
 * Depth is only ever half-asserted by a scene that paints entirely behind its
 * own content: the grasses were already the front plane by every other
 * signal — largest, fastest, most displaced — and then stopped dead at the
 * button's edge, which is exactly where a viewer's eye checks the claim. A
 * plume crossing a corner of the CTA is what settles it, and costs nothing:
 * the whole scene is `pointer-events: none`, so the button underneath keeps
 * its full 44px target and every tap that lands on grass reaches it anyway.
 *
 * Ordering, not z-index arithmetic, is what puts these in front. The
 * foreground container carries `z-index: 1` — the SAME layer as
 * `.landing__hero` — and wins on source order, which leaves the brand header's
 * `z-index: 2` still above it. Bidding 2 here would put grass over the
 * wordmark; bidding 3 would put it over the drawer.
 */
export const FOREGROUND_LAYERS = [
    {
        key: 'grassLeft',
        depth: 1.35,
        widths: [290, 580],
        group: 'layers',
        name: 'grass-left',
        sizes: '(max-width: 640px) 46vw, 265px',
    },
    {
        key: 'grassRight',
        depth: 1.5,
        widths: [320, 640],
        group: 'layers',
        name: 'grass-right',
        sizes: '(max-width: 640px) 50vw, 295px',
    },
];

export const ASSET_ROOT = '/assets/landing/plant-habb-me';

/**
 * A plain responsive `<img>`, deliberately NOT a `<picture>` with a legacy
 * fallback source.
 *
 * The `<picture>` version measurably fetched BOTH the chosen WebP and the
 * `<img src>` JPEG — 279 KB of hero image downloaded and never displayed, on the
 * one screen whose load time matters most. WebP has been supported by every
 * browser this product targets since 2020, so the fallback bought nothing and
 * cost a duplicate request.
 */
export function SceneLayer({ layer }) {
    const assetUrl = (width) => `${ASSET_ROOT}/${layer.group}/${layer.name}-${width}.webp`;
    const smallestWidth = layer.widths[0];

    return (
        <div
            className={`landing-scene__layer landing-scene__layer--${layer.key}`}
            style={{ '--depth': layer.depth }}
        >
            <img
                className="landing-scene__art"
                src={assetUrl(smallestWidth)}
                srcSet={layer.widths.map((width) => `${assetUrl(width)} ${width}w`).join(', ')}
                sizes={layer.sizes}
                alt=""
                /* EVERY layer loads eagerly. Two reasons, and the second is the
                   one that bites: all four are part of the first screenful, so
                   there is nothing to defer; and `loading="lazy"` inside a
                   `position: fixed`, `overflow: hidden`, transformed container is
                   where iOS Safari's lazy loading is least reliable — the
                   accents simply never appeared on device while loading fine in
                   every desktop engine. */
                loading="eager"
                /* 'garden', the actual background key — this said 'landscape',
                   a key no layer has answered to for two scene iterations, so
                   the biggest image on the screen was fetching at 'auto'. */
                fetchPriority={layer.key === 'garden' ? 'high' : 'auto'}
                decoding="async"
                draggable="false"
            />
        </div>
    );
}
